import type { BusinessStatusPathAlignment } from "@/utils/businessStatusPathAlignment";
import { getBusinessStatusPathAlignment } from "@/utils/businessStatusPathAlignment";

export type { StatusConfigPathEntry } from "@/utils/businessStatusPathAlignment";

import { getActiveProjectsTabQuerySlugFromStaticTabKey } from "@/utils/activeProjectsTabQuerySlug";

/**
 * Extracts the pathname from a full URL, a relative href, or a bare pathname.
 * Query strings and hashes are ignored (matching logic in `getBusinessStatusPathAlignment`).
 */
export function pathnameFromHref(href: string | null | undefined): string {
  if (href == null) return "";
  const s = String(href).trim();
  if (!s) return "";

  try {
    if (/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(s)) {
      return new URL(s).pathname;
    }
  } catch {
    // treat as relative path below
  }

  const noHash = s.split("#")[0] ?? "";
  const noQuery = noHash.split("?")[0] ?? "";
  return noQuery;
}

export type StatusConfigUrlEvaluation = {
  /** Status after normalization (uppercase, underscores → spaces), matching `status-config.json` keys. */
  normalizedStatus: string;
  /** Whether this status has a row in `status-config.json`. */
  hasStatusConfigEntry: boolean;
  /**
   * When there is a config row: current path is under `redirect_url` or one of `allowed_path_prefixes`.
   * When there is no row: always true (config does not constrain the route).
   */
  isApplicableForCurrentUrl: boolean;
  /**
   * True when the status has config and the current path matches none of the applicable prefixes.
   */
  needsRedirectToStatusHome: boolean;
  /**
   * When `needsRedirectToStatusHome` is true: canonical `redirect_url` from `status-config.json`
   * (e.g. `router.replace(redirectUrl)`). Otherwise `null`.
   */
  redirectUrl: string | null;
  /** Full alignment object for tab prefixes, entry payload, etc. */
  alignment: BusinessStatusPathAlignment;
};

/**
 * Uses `status-config.json` and the current URL path to decide whether the given status
 * is valid on this page or should redirect to the configured workflow module.
 *
 * @param status — Raw business / assessment status string.
 * @param currentPathOrHref — Prefer `usePathname()` in the App Router. You may also pass
 *   `window.location.href`, a pathname with query, or a relative href; only the pathname segment
 *   is used for prefix matching.
 */
export function evaluateStatusForStatusConfigUrl(
  status: string | null | undefined,
  currentPathOrHref: string | null | undefined
): StatusConfigUrlEvaluation {
  const pathname = pathnameFromHref(currentPathOrHref);
  const alignment = getBusinessStatusPathAlignment(status, pathname);

  const needsRedirect = alignment.shouldRedirectToStatusConfiguredHome;
  const rawRedirect =
    needsRedirect && alignment.statusConfigEntry
      ? String(alignment.statusConfigEntry.redirect_url ?? "").trim()
      : "";
  const redirectUrl = rawRedirect.length > 0 ? rawRedirect : null;

  return {
    normalizedStatus: alignment.normalizedStatus,
    hasStatusConfigEntry: alignment.hasStatusConfigEntry,
    isApplicableForCurrentUrl: alignment.isCurrentPathUnderExpectedModule,
    needsRedirectToStatusHome: needsRedirect,
    redirectUrl,
    alignment,
  };
}

/**
 * If the status does not belong on the current path per `status-config.json`, returns that row’s
 * `redirect_url` so you can `router.replace(url)`. Otherwise `null`.
 */
export function getStatusConfigRedirectUrlIfWrongModule(
  status: string | null | undefined,
  currentPathOrHref: string | null | undefined
): string | null {
  return evaluateStatusForStatusConfigUrl(status, currentPathOrHref).redirectUrl;
}

export type StatusConfigRedirectWithVendorContextParams = {
  status: string | null | undefined;
  currentPathOrHref: string | null | undefined;
  productId: string | number | null | undefined;
  vendorId: string | number | null | undefined;
  /** Same id the app uses in Active Projects URLs (`vendorAssessmentId` query). */
  vendorAssessmentId: string | number | null | undefined;
  /**
   * Current Active Projects main tab key (`VendorTabs` `tabKey`, or `activeProjectsTabKey` from context)
   * used only to build the `tab` query — never read from `status-config.json`.
   */
  activeProjectsStaticTabKey?: string | null;
};

/**
 * When the status belongs on another module (per `status-config.json` vs current path), returns
 * `{redirect_url}/{productId}?vendorId=…&vendorAssessmentId=…` and, when the current tab key maps
 * to a slug, `&tab=<slug>` from {@link getActiveProjectsTabQuerySlugFromStaticTabKey}.
 * Returns `null` when no redirect is needed or `productId` / base `redirect_url` is missing.
 */
export function getStatusConfigRedirectHrefWithVendorContext(
  params: StatusConfigRedirectWithVendorContextParams
): string | null {
  const {
    status,
    currentPathOrHref,
    productId,
    vendorId,
    vendorAssessmentId,
    activeProjectsStaticTabKey,
  } = params;
  const ev = evaluateStatusForStatusConfigUrl(status, currentPathOrHref);
  if (!ev.needsRedirectToStatusHome || !ev.alignment.statusConfigEntry) {
    return null;
  }

  const entry = ev.alignment.statusConfigEntry;
  const base = String(entry.redirect_url ?? "").trim().replace(/\/+$/, "");
  const pid = String(productId ?? "").trim();
  if (!base || !pid) {
    return null;
  }

  const path = `${base}/${encodeURIComponent(pid)}`;
  const q = new URLSearchParams();

  const vid = vendorId != null && String(vendorId).trim() !== "" ? String(vendorId).trim() : "";
  const aid =
    vendorAssessmentId != null && String(vendorAssessmentId).trim() !== ""
      ? String(vendorAssessmentId).trim()
      : "";
  if (vid) {
    q.set("vendorId", vid);
  }
  if (aid) {
    q.set("vendorAssessmentId", aid);
  }

  const tabQuery = getActiveProjectsTabQuerySlugFromStaticTabKey(activeProjectsStaticTabKey);
  if (tabQuery) {
    q.set("tab", tabQuery);
  }

  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
}
